// action/send-internal-notification.tsx

import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { CardShell, FieldLabel } from "./card-handler";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";

const CHANNEL_OPTIONS = [
  { label: "WhatsApp", value: "WhatsApp" },
  { label: "Email", value: "Email" },
  { label: "SMS", value: "SMS" },
];

const TEAM_OPTIONS = [
  { label: "Support Team", value: "support" },
  { label: "Sales Team", value: "sales" },
  { label: "Technical Team", value: "technical" },
];

interface SendInternalNotificationCardProps {
  node: ActionNode;
  templates?: TemplateModule[];
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const SendInternalNotificationCard: React.FC<SendInternalNotificationCardProps> = ({
  node,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const config = node.config ?? {};

  const update = (patch: Record<string, any>) => onUpdate({ ...config, ...patch });

  const availableTemplates = templates.filter(t => 
    !t.channel || t.channel.toLowerCase() === (config.channel || "whatsapp").toLowerCase()
  );

  const selectedTemplate = useMemo(() => {
    const id = config.templateId ?? "";
    const name = config.templateName ?? "";
    return availableTemplates.find(t => t.id === id || t.template_name === name) ?? null;
  }, [availableTemplates, config]);

  const handleChannelChange = (channel: string) => {
    update({ channel, templateId: "", templateName: "" });
  };

  const handleTemplateChange = (templateId: string) => {
    const tpl = availableTemplates.find(t => t.id === templateId);
    update({
      templateId: tpl?.id ?? "",
      templateName: tpl?.template_name ?? "",
      message: tpl?.content ?? "",
    });
  };

  return (
    <CardShell
      title="Send Internal Notification"
      description="Notify Your Support Team"
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        <div className="mb-4">
          <FieldLabel>Select Channel</FieldLabel>
          <div className="relative">
            <select
              value={config.channel ?? "WhatsApp"}
              onChange={(e) => handleChannelChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {CHANNEL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        <div className="mb-4">
          <FieldLabel>Select Team</FieldLabel>
          <div className="relative">
            <select
              value={config.team ?? ""}
              onChange={(e) => update({ team: e.target.value })}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="">— Select Team —</option>
              {TEAM_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        <div className="mb-4">
          <FieldLabel>Select Template</FieldLabel>
          <div className="relative">
            <select
              value={config.templateId ?? ""}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              <option value="">— Select Template —</option>
              {availableTemplates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.template_name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {selectedTemplate && (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
            {selectedTemplate.content || config.message}
          </div>
        )}
    </CardShell>
  );
};